// components/mascot/JIBI.tsx
import { useId } from "react";
import type { JibiProps } from "./types";
import { JIBI_SIZE_MAP } from "./types";

/**
 * JIBI — Mascot chính của JB AI
 *
 * Thiết kế: chibi đầu to, robot bay (không chân), tay mitten (palm + 3 ngón rời),
 * anten giữa đầu, hologram base, screen face với mắt cyan glow.
 *
 * @example
 * <JIBI pose="waving" size="md" animate />
 */
export default function JIBI({
  pose = "waving",
  size = "md",
  animate = true,
  className = "",
}: JibiProps) {
  const px = JIBI_SIZE_MAP[size];
  const reactId = useId();
  // Unique ID để tránh conflict gradient khi render nhiều JIBI cùng lúc
  const uid = `jibi-${reactId.replace(/:/g, "")}`;

  return (
    <svg
      viewBox="0 0 260 280"
      width={px}
      height={(px * 280) / 260}
      className={className}
      role="img"
      aria-label="JIBI - trợ lý AI của JB"
      style={{ overflow: "visible" }}
    >
      <defs>
        <radialGradient id={`${uid}-badge`} cx="50%" cy="50%">
          <stop offset="0%" stopColor="#00D4FF" />
          <stop offset="100%" stopColor="#0066FF" />
        </radialGradient>
        <radialGradient id={`${uid}-eye`} cx="50%" cy="50%">
          <stop offset="0%" stopColor="#7DF9FF" />
          <stop offset="70%" stopColor="#00D4FF" />
          <stop offset="100%" stopColor="#0099CC" />
        </radialGradient>
        <linearGradient id={`${uid}-screen`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a1f2e" />
          <stop offset="100%" stopColor="#000814" />
        </linearGradient>

        {/* Bàn tay: palm to + 1 ngón cái LỚN (bên phải palm) + 3 ngón nhỏ dính mép trên
            — Tay trái tự lật qua scale(-1 1) ở transform wrapper → ngón cái tự chuyển sang bên kia. */}
        <g id={`${uid}-hand`}>
          {/* Palm */}
          <circle cx="0" cy="0" r="20" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />
          {/* Ngón cái (to) - bên phải palm, hơi chếch lên */}
          <circle cx="20" cy="-6" r="10" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />
          {/* 3 ngón nhỏ - dính mép trên palm, cạnh nhau */}
          <circle cx="-12" cy="-17" r="6.5" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />
          <circle cx="0" cy="-19" r="6.5" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />
          <circle cx="12" cy="-17" r="6.5" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />
          {/* Highlight palm */}
          <circle cx="-6" cy="-5" r="5" fill="#ffffff" opacity="0.7" />
        </g>
      </defs>

      {/* Hologram base */}
      <ellipse cx="130" cy="262" rx="50" ry="9" fill="#00D4FF" opacity="0.25" />
      <ellipse cx="130" cy="262" rx="36" ry="6" fill="#00D4FF" opacity="0.5" />
      <ellipse cx="130" cy="262" rx="22" ry="4" fill="#00D4FF" opacity="0.8" />

      {/* Sparkles */}
      <text x="22" y="80" fontSize="20" fill="#93C5FD" opacity="0.8">✦</text>
      <text x="220" y="60" fontSize="16" fill="#93C5FD" opacity="0.7">✦</text>
      <text x="30" y="200" fontSize="14" fill="#93C5FD" opacity="0.6">✦</text>
      <text x="222" y="200" fontSize="12" fill="#93C5FD" opacity="0.7">✦</text>

      {/* Toàn bộ robot float up/down */}
      <g>
        {animate && (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 -6; 0 0"
            dur="2.8s"
            repeatCount="indefinite"
          />
        )}

        {/* ANTENNA */}
        <line x1="130" y1="30" x2="130" y2="2" stroke="#CBD5E1" strokeWidth="5" strokeLinecap="round" />
        <circle cx="130" cy="2" r="10" fill="#00D4FF">
          {animate && <animate attributeName="opacity" values="1;0.5;1" dur="1.6s" repeatCount="indefinite" />}
        </circle>
        <circle cx="130" cy="2" r="16" fill="#00D4FF" opacity="0.3">
          {animate && <animate attributeName="opacity" values="0.3;0.1;0.3" dur="1.6s" repeatCount="indefinite" />}
        </circle>
        <circle cx="127" cy="-1" r="3" fill="#ffffff" opacity="0.8" />

        {/* HEAD */}
        <rect x="60" y="32" width="140" height="120" rx="30" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />

        {/* Screen */}
        <rect x="72" y="48" width="116" height="92" rx="22" fill={`url(#${uid}-screen)`} />
        <path d="M 76 52 Q 80 50 84 52 L 84 74 Q 80 76 76 74 Z" fill="#ffffff" opacity="0.08" />

        {/* Eyes */}
        <ellipse cx="104" cy="90" rx="14" ry="18" fill={`url(#${uid}-eye)`} />
        <ellipse cx="156" cy="90" rx="14" ry="18" fill={`url(#${uid}-eye)`} />
        <ellipse cx="108" cy="82" rx="4" ry="6" fill="#ffffff" opacity="0.9" />
        <ellipse cx="160" cy="82" rx="4" ry="6" fill="#ffffff" opacity="0.9" />

        {/* Happy mouth */}
        <path d="M 118 118 Q 130 130 142 118" stroke="#00D4FF" strokeWidth="3.5" fill="none" strokeLinecap="round" />

        {/* Neck */}
        <rect x="118" y="150" width="24" height="8" rx="3" fill="#E2E8F0" />

        {/* BODY */}
        <ellipse cx="130" cy="200" rx="42" ry="46" fill="#F5F7FA" stroke="#CBD5E1" strokeWidth="2" />

        {/* Badge JB */}
        <rect x="110" y="185" width="40" height="40" rx="9" fill={`url(#${uid}-badge)`} />
        <rect x="110" y="185" width="40" height="40" rx="9" fill="none" stroke="#00D4FF" strokeWidth="2" opacity="0.6" />
        <text
          x="130"
          y="212"
          fontSize="18"
          fontWeight="700"
          fill="#ffffff"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
        >
          JB
        </text>

        {/* LEFT HAND (tĩnh, ngón cái quay vào thân) */}
        <g transform="translate(62 210)">
          <use href={`#${uid}-hand`} />
        </g>

        {/* RIGHT HAND — pose-dependent, lật scale(-1 1) để ngón cái quay vào thân */}
        {pose === "waving" ? (
          <g transform="translate(198 192) scale(-1 1)">
            <g>
              {animate && (
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  values="-30; 30; -30"
                  dur="1.3s"
                  repeatCount="indefinite"
                />
              )}
              <g transform="translate(0 -22)">
                <use href={`#${uid}-hand`} />
              </g>
            </g>
          </g>
        ) : (
          <g transform="translate(198 210) scale(-1 1)">
            <use href={`#${uid}-hand`} />
          </g>
        )}
      </g>
    </svg>
  );
}